import { z } from "zod";
import { idSchema, reasonSchema } from "@amber/contracts";
import { RELATIONSHIP_LABELS, TIER_LABELS, relationshipSchema } from "./monitoring.js";
import { cadenceTierSchema, sourceKindSchema, type SOURCE_KINDS } from "./sources.js";

export const SOURCE_KIND_EDIT_LABELS: Record<(typeof SOURCE_KINDS)[number], string> = {
  channel: "Channel",
  playlist: "Playlist",
  account: "Account",
  programme: "Programme",
  feed: "Feed",
  official_record: "Official record",
  website: "Website",
  news_search: "News search",
};

export const QUOTA_GROUPS = ["youtube_data_api", "twitterapi_io"] as const;
export const QUOTA_GROUP_LABELS: Record<(typeof QUOTA_GROUPS)[number], string> = {
  youtube_data_api: "YouTube Data API",
  twitterapi_io: "twitterapi.io",
};

export const sourceEditPersonSchema = z.strictObject({
  profile_id: idSchema("prf"),
  relationship: relationshipSchema.nullable(),
  in_plan: z.boolean(),
  required: z.boolean(),
});
export type SourceEditPerson = z.infer<typeof sourceEditPersonSchema>;

export const sourceEditBreakSchema = z.strictObject({
  id: idSchema("brk").nullable(),
  kind: z.enum(["recorded_break", "expected_quiet"]),
  from: z.iso.date(),
  to: z.iso.date().nullable(),
  label: z.string().trim().min(1, "Give the break a label").max(200),
  ended: z.boolean(),
});
export type SourceEditBreak = z.infer<typeof sourceEditBreakSchema>;

export const sourceEditFieldsSchema = z.strictObject({
  title: z.string().trim().min(1, "Give the source a name").max(300),
  kind: sourceKindSchema,
  people: z.array(sourceEditPersonSchema),
  cadence_tier: cadenceTierSchema,
  known_breaks: z.array(sourceEditBreakSchema),
  quota_group: z.enum(QUOTA_GROUPS).nullable(),
  owner_team: z.string().trim().max(120).nullable(),
});
export type SourceEditFields = z.infer<typeof sourceEditFieldsSchema>;

export const sourceEditViewSchema = z.strictObject({
  id: idSchema("src"),
  platform: z.string(),
  platform_label: z.string(),
  collected: z.boolean(),
  shared_plan: z.boolean(),
  revision: z.number().int().positive(),
  plans_version: z.string(),
  names: z.record(z.string(), z.string()),
  fields: sourceEditFieldsSchema,
});
export type SourceEditView = z.infer<typeof sourceEditViewSchema>;

export const editSourceRequestSchema = sourceEditFieldsSchema.extend({
  base_revision: z.number().int().positive(),
  base_plans: z.string(),
  reason: reasonSchema,
});
export type EditSourceRequest = z.infer<typeof editSourceRequestSchema>;

export interface SourceEditChange {
  readonly key: string;
  readonly label: string;
  readonly before: string;
  readonly after: string;
  readonly warning: string | null;
}

export function personEditText(p: SourceEditPerson | undefined, tier?: string): string {
  if (!p) return "Not linked";
  const relationship = p.relationship ? RELATIONSHIP_LABELS[p.relationship] : "No relationship";
  if (!p.in_plan) return `${relationship} · not in plan`;
  return [relationship, p.required ? "in plan, required route" : "in plan", ...(tier ? [tier] : [])].join(" · ");
}

export function breakEditText(b: SourceEditBreak): string {
  return `${b.label} (${b.from} – ${b.to ?? "until further notice"}${b.ended ? ", ended" : ""})`;
}

function breaksText(breaks: readonly SourceEditBreak[]): string {
  return breaks.length === 0 ? "None" : breaks.map(breakEditText).join("; ");
}

function personChanges(
  before: SourceEditFields,
  after: SourceEditFields,
  nameOf: (id: string) => string,
): SourceEditChange[] {
  const ids = [...new Set([...before.people, ...after.people].map((p) => p.profile_id))];
  return ids.flatMap((id) => {
    const was = before.people.find((p) => p.profile_id === id);
    const now = after.people.find((p) => p.profile_id === id);
    const b = personEditText(was);
    const a = now ? personEditText(now) : "Removed";
    if (b === a) return [];
    const leaves = (was?.in_plan ?? false) && !(now?.in_plan ?? false);
    return [
      {
        key: `person:${id}`,
        label: nameOf(id),
        before: b,
        after: a,
        warning: leaves
          ? `${nameOf(id)} will no longer get new items from this source; nothing already collected is removed`
          : null,
      },
    ];
  });
}

const optional = (value: string | null, none: string) => (value === null || value === "" ? none : value);
const quotaText = (q: SourceEditFields["quota_group"]) => (q ? QUOTA_GROUP_LABELS[q] : "Platform default");

/** The before → after list an edit shows and records; the panel and the server compute it the same way. */
export function sourceEditChanges(
  before: SourceEditFields,
  after: SourceEditFields,
  nameOf: (id: string) => string,
): SourceEditChange[] {
  const scalar: [string, string, string, string][] = [
    ["title", "Name", before.title, after.title.trim()],
    ["kind", "Kind", SOURCE_KIND_EDIT_LABELS[before.kind], SOURCE_KIND_EDIT_LABELS[after.kind]],
    ["cadence_tier", "Cadence tier", TIER_LABELS[before.cadence_tier], TIER_LABELS[after.cadence_tier]],
    ["known_breaks", "Known breaks", breaksText(before.known_breaks), breaksText(after.known_breaks)],
    ["quota_group", "Quota group", quotaText(before.quota_group), quotaText(after.quota_group)],
    [
      "owner_team",
      "Owner team",
      optional(before.owner_team, "None"),
      optional(after.owner_team?.trim() ?? null, "None"),
    ],
  ];
  const fields = scalar
    .filter(([, , b, a]) => b !== a)
    .map(([key, label, b, a]) => ({ key, label, before: b, after: a, warning: null }));
  const head = (c: SourceEditChange) => c.key === "title" || c.key === "kind";
  return [...fields.filter(head), ...personChanges(before, after, nameOf), ...fields.filter((c) => !head(c))];
}

function pick<T>(opened: T, current: T, draft: T): T {
  return JSON.stringify(draft) === JSON.stringify(opened) ? current : draft;
}

function rebasePeople(
  opened: readonly SourceEditPerson[],
  current: readonly SourceEditPerson[],
  draft: readonly SourceEditPerson[],
): SourceEditPerson[] {
  const ids = [...new Set([...current, ...draft].map((p) => p.profile_id))];
  return ids.flatMap((id) => {
    const find = (list: readonly SourceEditPerson[]) => list.find((p) => p.profile_id === id) ?? null;
    const chosen = pick(find(opened), find(current), find(draft));
    return chosen ? [chosen] : [];
  });
}

/** Keep my draft after a conflict: the user's own changes stay, everything they didn't touch takes the saved value. */
export function rebaseSourceDraft(
  opened: SourceEditFields,
  current: SourceEditFields,
  draft: SourceEditFields,
): SourceEditFields {
  return {
    title: pick(opened.title, current.title, draft.title),
    kind: pick(opened.kind, current.kind, draft.kind),
    people: rebasePeople(opened.people, current.people, draft.people),
    cadence_tier: pick(opened.cadence_tier, current.cadence_tier, draft.cadence_tier),
    known_breaks: pick(opened.known_breaks, current.known_breaks, draft.known_breaks),
    quota_group: pick(opened.quota_group, current.quota_group, draft.quota_group),
    owner_team: pick(opened.owner_team, current.owner_team, draft.owner_team),
  };
}
